(() => {
  'use strict';

  const VERSION = '2026-09-24.3-real-submit';
  const ROOT_SELECTOR = '[data-pms-exit-survey]';
  const SHOWN_KEY = 'pms:exit-survey:v1:last-shown';
  const SUBMITTED_KEY = 'pms:exit-survey:v1:submitted';
  const VISITOR_KEY = 'pms:exit-survey:v1:visitor-id';
  const MOBILE_DWELL_SESSION_KEY = 'pms:exit-survey:v1:mobile-visible-ms';
  const RESPONSE_ID_SESSION_KEY = 'pms:exit-survey:v1:response-id';
  const SUPPRESS_MS = 7 * 24 * 60 * 60 * 1000;
  const MIN_DWELL_MS = 10000;

  const safeGet = key => {
    try { return localStorage.getItem(key); } catch { return null; }
  };

  const safeSet = (key, value) => {
    try { localStorage.setItem(key, value); } catch {}
  };

  const safeSessionGet = key => {
    try { return sessionStorage.getItem(key); } catch { return null; }
  };

  const safeSessionSet = (key, value) => {
    try { sessionStorage.setItem(key, value); } catch {}
  };

  const safeSessionRemove = key => {
    try { sessionStorage.removeItem(key); } catch {}
  };

  const isPreview = () => {
    const params = new URLSearchParams(window.location.search);
    const themeRole = String(window.Shopify?.theme?.role || '').toLowerCase();
    const hostname = String(window.location.hostname || '').toLowerCase();
    const isShopifyPreviewHost = hostname === 'shopifypreview.com' || hostname.endsWith('.shopifypreview.com');
    return (
      params.has('preview_theme_id') ||
      Boolean(window.Shopify?.designMode) ||
      themeRole === 'unpublished' ||
      isShopifyPreviewHost
    );
  };

  const visitorId = () => {
    let id = safeGet(VISITOR_KEY);
    if (!id) {
      id = window.crypto?.randomUUID?.() || 'survey-' + Date.now() + '-' + Math.random().toString(16).slice(2);
      safeSet(VISITOR_KEY, id);
    }
    return id;
  };

  const device = () => {
    if (matchMedia('(max-width: 749px)').matches) return 'mobile';
    if (matchMedia('(max-width: 1024px)').matches) return 'tablet';
    return 'desktop';
  };

  class ExitSurvey {
    constructor(root) {
      this.root = root;
      this.dialog = root.querySelector('.pms-exit-survey__dialog');
      this.form = root.querySelector('[data-pms-exit-survey-form]');
      this.question = root.querySelector('[data-pms-exit-survey-question]');
      this.thanks = root.querySelector('[data-pms-exit-survey-thanks]');
      this.extra = root.querySelector('[data-pms-exit-survey-extra]');
      this.more = root.querySelector('[data-pms-exit-survey-more]');
      this.error = root.querySelector('[data-pms-exit-survey-error]');
      this.submitButton = root.querySelector('[data-pms-exit-survey-submit]');
      this.submitText = root.querySelector('[data-pms-exit-survey-submit-text]');
      this.rewardForm = root.querySelector('[data-pms-exit-survey-reward-form]');
      this.rewardEmail = root.querySelector('[data-pms-exit-survey-reward-email]');
      this.rewardError = root.querySelector('[data-pms-exit-survey-reward-error]');
      this.rewardSubmitButton = root.querySelector('[data-pms-exit-survey-reward-submit]');
      this.rewardSubmitText = root.querySelector('[data-pms-exit-survey-reward-submit-text]');
      this.rewardContent = root.querySelector('[data-pms-exit-survey-reward-content]');
      this.rewardSuccess = root.querySelector('[data-pms-exit-survey-reward-success]');
      this.responseId = String(safeSessionGet(RESPONSE_ID_SESSION_KEY) || '');
      this.preview = isPreview();
      this.editorForceShow = Boolean(window.Shopify?.designMode) && root.dataset.editorForceShow === 'true';
      this.editorView = root.dataset.editorView === 'thanks' ? 'thanks' : 'question';
      this.mobileEnabled = root.dataset.mobileEnabled !== 'false';
      this.mobileDelayMs = Math.max(5000, Number(root.dataset.mobileDelaySeconds || 120) * 1000);
      this.mobileVisibleMs = Math.max(0, Number(safeSessionGet(MOBILE_DWELL_SESSION_KEY) || 0));
      this.mobileTimer = null;
      this.opened = false;
      this.submitted = false;
      this.armed = false;
      this.startedAt = Date.now();
      this.previousFocus = null;
      this.abortController = new AbortController();
    }

    mount() {
      if (!this.form || !this.dialog) return;

      if (this.root.dataset.previewOnly === 'true' && !this.preview) return;

      const signal = this.abortController.signal;

      document.addEventListener('keydown', event => this.onKeyDown(event), { signal });

      this.root.querySelectorAll('[data-pms-exit-survey-close]').forEach(button => {
        button.addEventListener('click', () => this.close(), { signal });
      });

      this.more?.addEventListener('click', () => this.toggleExtra(), { signal });
      this.form.addEventListener('submit', event => this.onSubmit(event), { signal });
      this.rewardForm?.addEventListener('submit', event => this.onRewardSubmit(event), { signal });

      this.root.dataset.pmsExitSurveyMounted = VERSION;

      if (this.editorForceShow) {
        this.openEditorPreview();
      } else {
        if (!this.preview && this.shouldSuppress()) return;

        if (this.isMobile()) {
          if (!this.mobileEnabled) return;
          this.startMobileDwellTimer(signal);
        } else if (this.isDesktop()) {
          if (!String(this.root.dataset.productId || '').trim()) return;
          document.addEventListener('mouseout', event => this.onMouseOut(event), { signal });
          document.documentElement.addEventListener('mouseleave', event => this.onMouseLeave(event), { signal });
          document.addEventListener('mousemove', event => {
            this.armWhenBrowsing(event);
            this.onTopEdgeMove(event);
          }, { passive: true, signal });
        } else {
          return;
        }
      }

      if (this.preview) {
        console.info('[PMS Exit Survey]', {
          stage: 'mounted',
          version: VERSION,
          preview: true,
          editor_force_show: this.editorForceShow,
          editor_view: this.editorView,
          theme_role: window.Shopify?.theme?.role || ''
        });
      }
    }

    openEditorPreview() {
      this.opened = true;
      this.triggerType = 'theme_editor_preview';
      this.previousFocus = null;

      if (this.editorView === 'thanks') {
        this.question.hidden = true;
        this.thanks.hidden = false;
      } else {
        this.question.hidden = false;
        this.thanks.hidden = true;
      }

      this.root.hidden = false;
      document.documentElement.classList.add('pms-exit-survey-open');
      requestAnimationFrame(() => {
        this.dialog?.focus();
        if (!this.dialog) return;
        this.dialog.scrollTop = this.isMobile() && this.editorView !== 'thanks'
          ? this.dialog.scrollHeight
          : 0;
      });
    }

    isMobile() {
      return matchMedia('(max-width: 749px)').matches;
    }

    isDesktop() {
      return matchMedia('(hover: hover) and (pointer: fine)').matches && window.innerWidth >= 900;
    }

    startMobileDwellTimer(signal) {
      if (this.mobileTimer) return;

      const tickMs = 1000;
      this.mobileTimer = window.setInterval(() => {
        if (this.opened || this.submitted) {
          window.clearInterval(this.mobileTimer);
          this.mobileTimer = null;
          return;
        }

        if (document.visibilityState === 'visible') {
          this.mobileVisibleMs += tickMs;
          safeSessionSet(MOBILE_DWELL_SESSION_KEY, String(this.mobileVisibleMs));
        }

        if (this.mobileVisibleMs >= this.mobileDelayMs) {
          window.clearInterval(this.mobileTimer);
          this.mobileTimer = null;
          safeSessionRemove(MOBILE_DWELL_SESSION_KEY);
          this.open('sitewide_dwell_mobile');
        }
      }, tickMs);

      signal.addEventListener('abort', () => {
        if (this.mobileTimer) {
          window.clearInterval(this.mobileTimer);
          this.mobileTimer = null;
        }
      }, { once: true });
    }

    shouldSuppress() {
      if (safeGet(SUBMITTED_KEY) === 'true') return true;
      const timestamp = Number(safeGet(SHOWN_KEY) || 0);
      return Number.isFinite(timestamp) && timestamp > 0 && (Date.now() - timestamp) < SUPPRESS_MS;
    }

    armWhenBrowsing(event) {
      if (this.armed) return;
      if (typeof event.clientY === 'number' && event.clientY > 150) {
        this.armed = true;
      }
    }

    canTrigger() {
      if (this.opened || this.submitted || !this.armed) return false;
      if (Date.now() - this.startedAt < MIN_DWELL_MS) return false;
      if (document.visibilityState !== 'visible') return false;
      return true;
    }

    onMouseOut(event) {
      if (!this.canTrigger()) return;
      if (event.relatedTarget || event.toElement) return;
      if (event.clientY > 32) return;
      this.open('product_page_exit_desktop');
    }

    onMouseLeave(event) {
      if (!this.canTrigger()) return;
      if (typeof event.clientY === 'number' && event.clientY > 32) return;
      this.open('product_page_exit_desktop');
    }

    onTopEdgeMove(event) {
      if (!this.canTrigger()) return;
      if (event.clientY > 4) return;
      if (typeof event.movementY === 'number' && event.movementY >= 0) return;
      this.open('product_page_exit_desktop');
    }

    open(triggerType) {
      if (this.opened) return;

      this.opened = true;
      this.triggerType = triggerType;
      this.previousFocus = document.activeElement;

      this.root.hidden = false;
      document.documentElement.classList.add('pms-exit-survey-open');

      if (!this.preview) safeSet(SHOWN_KEY, String(Date.now()));
      safeSessionRemove(MOBILE_DWELL_SESSION_KEY);

      requestAnimationFrame(() => {
        this.dialog?.focus();
        if (!this.dialog) return;
        this.dialog.scrollTop = this.isMobile() ? this.dialog.scrollHeight : 0;
      });

      try {
        window.dispatchEvent(new CustomEvent('pms:exit-survey:shown', {
          detail: {
            survey_version: this.root.dataset.surveyVersion || 'exit-v1',
            trigger_type: triggerType
          }
        }));
      } catch {}
    }

    close() {
      if (!this.opened) return;

      this.opened = false;
      this.root.hidden = true;
      document.documentElement.classList.remove('pms-exit-survey-open');

      if (this.previousFocus instanceof HTMLElement) {
        this.previousFocus.focus({ preventScroll: true });
      }
    }

    onKeyDown(event) {
      if (this.opened && event.key === 'Escape') {
        event.preventDefault();
        this.close();
      }
    }

    toggleExtra() {
      const expanded = this.more?.getAttribute('aria-expanded') === 'true';

      this.more?.setAttribute('aria-expanded', expanded ? 'false' : 'true');
      if (this.extra) this.extra.hidden = expanded;

      const icon = this.root.querySelector('[data-pms-exit-survey-more-icon]');
      if (icon) icon.textContent = expanded ? '+' : '−';
    }

    selectedReasons() {
      return Array.from(
        this.form.querySelectorAll('input[name="reason"]:checked')
      ).map(input => input.value);
    }

    payload() {
      const params = new URLSearchParams(window.location.search);

      return {
        type: 'pms.exit-survey',
        survey_version: this.root.dataset.surveyVersion || 'exit-v1',
        anonymous_visitor_id: visitorId(),
        trigger_type: this.triggerType || 'product_page_exit_desktop',
        reasons: this.selectedReasons(),
        free_text: String(this.form.elements.note?.value || '').trim(),
        page_url: window.location.href,
        page_path: window.location.pathname,
        product_id: String(this.root.dataset.productId || ''),
        product_handle: String(this.root.dataset.productHandle || ''),
        product_title: String(this.root.dataset.productTitle || ''),
        product_type: String(this.root.dataset.productType || ''),
        has_cart_item: Number(this.root.dataset.cartItemCount || 0) > 0,
        cart_item_count: Number(this.root.dataset.cartItemCount || 0),
        device: device(),
        referrer: document.referrer || '',
        utm_source: params.get('utm_source') || '',
        utm_medium: params.get('utm_medium') || '',
        utm_campaign: params.get('utm_campaign') || '',
        timestamp: new Date().toISOString(),
        theme_role: String(window.Shopify?.theme?.role || ''),
        theme_id: String(window.Shopify?.theme?.id || ''),
        is_preview: this.preview,
        hostname: window.location.hostname,
        test_label: this.preview ? 'TEST_DO_NOT_COUNT' : ''
      };
    }

    async onSubmit(event) {
      event.preventDefault();

      if (this.submitted || this.submitButton?.disabled) return;

      const reasons = this.selectedReasons();
      const note = String(this.form.elements.note?.value || '').trim();

      if (!reasons.length && !note) {
        if (this.error && String(this.error.textContent || '').trim()) {
          this.error.hidden = false;
        }
        return;
      }

      if (this.error) this.error.hidden = true;
      this.submitButton.disabled = true;
      if (this.submitText) this.submitText.textContent = 'Sending…';

      const payload = this.payload();
      const endpoint = String(this.root.dataset.endpoint || '').trim();

      let httpStatus = null;
      let errorCode = '';
      try {
        let responseData = null;

        if (endpoint) {
          const response = await fetch(endpoint, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json'
            },
            body: JSON.stringify(payload),
            keepalive: true,
            credentials: 'omit'
          });

          httpStatus = response.status;
          responseData = await response.json().catch(() => ({}));
          errorCode = this.safeErrorCode(responseData?.error);
          if (!response.ok || responseData.ok !== true) {
            throw new Error('survey_http_' + response.status);
          }

        } else {
          throw new Error('survey_endpoint_not_configured');
        }

        this.responseId = String(responseData?.response_id || responseData?.submission_id || '');
        if (!/^[0-9a-f]{8}-(?:[0-9a-f]{4}-){3}[0-9a-f]{12}$/i.test(this.responseId)) throw new Error('invalid_response_id');
        if (this.responseId) {
          safeSessionSet(RESPONSE_ID_SESSION_KEY, this.responseId);
        }

        window.__pmsExitSurveyLastPayload = payload;

        try {
          sessionStorage.setItem('pms:exit-survey:v1:last-payload', JSON.stringify(payload));
        } catch {}

        this.submitted = true;

        if (!this.preview) safeSet(SUBMITTED_KEY, 'true');

        this.question.hidden = true;
        this.thanks.hidden = false;
        this.dialog.scrollTop = 0;
      } catch (error) {
        console.info('[PMS Exit Survey]', {
          stage: 'submit_failed',
          endpoint,
          hostname: window.location.hostname,
          theme_role: window.Shopify?.theme?.role || '',
          is_preview: this.preview,
          http_status: httpStatus,
          error_code: errorCode,
          error: String(error?.message || error)
        });

        if (this.error) {
          this.error.textContent = 'We could not send your feedback just now. You can close this window and continue shopping.';
          this.error.hidden = false;
        }
      } finally {
        this.submitButton.disabled = false;
        if (this.submitText) this.submitText.textContent = 'Share feedback';
      }
    }

    safeErrorCode(value) {
      return typeof value === 'string' && /^[a-z_]{1,80}$/.test(value) ? value : '';
    }

    async onRewardSubmit(event) {
      event.preventDefault();

      if (!this.rewardForm || !this.rewardEmail) return;
      if (this.rewardSubmitButton?.disabled) return;

      const email = String(this.rewardEmail.value || '').trim();

      if (!email || !this.rewardEmail.checkValidity()) {
        if (this.rewardError) {
          this.rewardError.textContent = 'Please enter a valid email address.';
          this.rewardError.hidden = false;
        }
        this.rewardEmail.focus();
        return;
      }

      if (this.rewardError) this.rewardError.hidden = true;
      if (this.rewardSubmitButton) this.rewardSubmitButton.disabled = true;

      const defaultButtonText = String(this.rewardSubmitText?.textContent || 'Send Me My Rewards');
      if (this.rewardSubmitText) this.rewardSubmitText.textContent = 'Sending…';

      const showSuccess = () => {
        if (this.rewardContent) this.rewardContent.hidden = true;
        if (this.rewardSuccess) this.rewardSuccess.hidden = false;
        this.dialog.scrollTop = 0;
      };

      const responseId = String(this.responseId || safeSessionGet(RESPONSE_ID_SESSION_KEY) || '').trim();
      const rewardEndpoint = String(this.root.dataset.rewardEndpoint || '').trim();

      if (!responseId || !rewardEndpoint) {
        if (this.rewardError) {
          this.rewardError.textContent = 'We could not prepare your reward just now. Please try again.';
          this.rewardError.hidden = false;
        }
        if (this.rewardSubmitButton) this.rewardSubmitButton.disabled = false;
        if (this.rewardSubmitText) this.rewardSubmitText.textContent = defaultButtonText;
        return;
      }

      let httpStatus = null;
      let errorCode = '';
      try {
        const response = await fetch(rewardEndpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({
            response_id: responseId,
            email,
            claimed_at: new Date().toISOString(),
            page_url: window.location.href
          }),
          credentials: 'omit'
        });

        httpStatus = response.status;
        const responseData = await response.json().catch(() => ({}));
        errorCode = this.safeErrorCode(responseData?.error);
        if (!response.ok || responseData.ok !== true) {
          throw new Error('reward_http_' + response.status);
        }

        showSuccess();
      } catch (error) {
        console.info('[PMS Exit Survey]', {
          stage: 'reward_claim_failed',
          endpoint: rewardEndpoint,
          hostname: window.location.hostname,
          theme_role: window.Shopify?.theme?.role || '',
          is_preview: this.preview,
          http_status: httpStatus,
          error_code: errorCode,
          error: String(error?.message || error)
        });

        if (this.rewardError) {
          this.rewardError.textContent = 'We could not save your email just now. Please try again.';
          this.rewardError.hidden = false;
        }
      } finally {
        if (this.rewardSubmitButton) this.rewardSubmitButton.disabled = false;
        if (this.rewardSubmitText) this.rewardSubmitText.textContent = defaultButtonText;
      }
    }
  }

  const mount = () => {
    document.querySelectorAll(ROOT_SELECTOR).forEach(root => {
      if (!root.dataset.pmsExitSurveyMounted) {
        new ExitSurvey(root).mount();
      }
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', mount, { once: true });
  } else {
    mount();
  }

  if (!window.__pmsExitSurveyThemeEditorListener) {
    window.__pmsExitSurveyThemeEditorListener = true;
    document.addEventListener('shopify:section:load', () => {
      document.documentElement.classList.remove('pms-exit-survey-open');
      mount();
    });
  }
})();
